/**
 * Labels Context (지갑 라벨 동적 적용)
 *
 * WHY: 운영자가 사이트설정 > 기타설정 > 라벨 설정에서 보유금/게임머니/포인트/롤링금
 *      등의 라벨을 사용자 정의할 수 있도록, 유저페이지 전반에서 같은 라벨을 노출.
 *      앱 마운트 시 1회 fetch + 5분 폴링 정도면 운영자 변경 후 반영 지연 허용 범위.
 *      현재는 단순 1회 fetch + 새로고침으로 반영 (폴링/소켓 푸시 미구현).
 *
 * 비인증 GET /api/settings/wallet-labels 호출 — 로그인 전후 모두 동작.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../services/api';

// 서버 미응답·오류 시 안전한 fallback. admin-api/user-api 의 DEFAULT_WALLET_LABELS 와 키·값 동일.
// 2026-05-01: members 4개 잔액 컬럼 1:1 매핑 (casino_money/slot_money 제거).
const DEFAULT_LABELS = {
  balance:    '보유금',
  game_money: '게임머니',
  point:      '포인트',
  rolling:    '롤링금',
};

const LabelsContext = createContext({ labels: DEFAULT_LABELS });

/**
 * useLabels Hook
 *
 * @returns {{ labels: Object, isLoading: boolean }}
 *   labels — 운영자 정의 라벨 (누락 키는 기본값으로 보충됨)
 *   isLoading — 첫 fetch 진행 중 여부 (대부분의 컴포넌트는 이걸 무시하고 labels.balance 등을 그대로 사용해도 OK,
 *               기본값으로 시작하므로 깜빡임 없음)
 *
 * @example
 *   const { labels } = useLabels();
 *   <span>{labels.balance}: {balance}원</span>
 */
export const useLabels = () => {
  const ctx = useContext(LabelsContext);
  return ctx;
};

export const LabelsProvider = ({ children }) => {
  const [labels, setLabels] = useState(DEFAULT_LABELS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchLabels = async () => {
      try {
        const res = await api.get('/settings/wallet-labels');
        if (cancelled) return;
        if (res?.data?.success && res.data.data?.labels) {
          // 누락 키는 기본값으로 보충 — UI 옵셔널 체이닝 없이 바로 사용 가능.
          setLabels({ ...DEFAULT_LABELS, ...res.data.data.labels });
        }
      } catch (err) {
        // 비인증 비치명 — 콘솔만 남기고 기본값 유지
        console.warn('[LabelsContext] wallet-labels 조회 실패, 기본값 사용:', err?.message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchLabels();
    return () => { cancelled = true; };
  }, []);

  return (
    <LabelsContext.Provider value={{ labels, isLoading }}>
      {children}
    </LabelsContext.Provider>
  );
};

export default LabelsContext;
